/**
 * DeadlinesList Component
 *
 * Displays deadlines grouped by status (Overdue, Due Soon, Upcoming).
 * Includes loading, error, and empty states.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { DeadlineCard } from './DeadlineCard';
import {
  DeadlineSummary,
  DeadlineType,
  DeadlineStatus,
} from './types';
import { AlertCircle, Calendar, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';

interface ListDeadlinesResponse {
  success: boolean;
  deadlines: DeadlineSummary[];
  totalCount: number;
  lastDocumentId?: string;
  error?: string;
}

interface CompleteDeadlineResponse {
  success: boolean;
  deadline?: DeadlineSummary;
  nextDeadline?: DeadlineSummary;
  error?: string;
}

interface DismissDeadlineResponse {
  success: boolean;
  deadline?: DeadlineSummary;
  error?: string;
}

interface DeadlinesListProps {
  caseFileId: string;
  statusFilter?: DeadlineStatus[];
  typeFilter?: DeadlineType[];
  startDate?: string;
  endDate?: string;
  onDeadlineClick?: (deadlineId: string) => void;
  onEditClick?: (deadlineId: string) => void;
  onExportClick?: (deadlineId: string) => void;
  refreshKey?: number;
}

interface GroupedDeadlines {
  overdue: DeadlineSummary[];
  due_soon: DeadlineSummary[];
  upcoming: DeadlineSummary[];
  completed: DeadlineSummary[];
  dismissed: DeadlineSummary[];
}

// Skeleton loading component
function DeadlineCardSkeleton(): JSX.Element {
  return (
    <div className="card animate-pulse border-l-4 border-gray-200">
      <div className="card-body">
        <div className="flex justify-between mb-2">
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
        </div>
        <div className="h-5 w-3/4 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-1/2 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

export function DeadlinesList({
  caseFileId,
  statusFilter,
  typeFilter,
  startDate,
  endDate,
  onDeadlineClick,
  onEditClick,
  onExportClick,
  refreshKey = 0,
}: DeadlinesListProps): JSX.Element {
  const [deadlines, setDeadlines] = useState<DeadlineSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Collapsed state for sections
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    completed: true,
    dismissed: true,
  });

  const fetchDeadlines = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const listDeadlines = httpsCallable<
        {
          caseFileId: string;
          status?: DeadlineStatus[];
          type?: DeadlineType[];
          startDate?: string;
          endDate?: string;
          limit?: number;
        },
        ListDeadlinesResponse
      >(functions, 'listDeadlines');

      const result = await listDeadlines({
        caseFileId,
        status: statusFilter,
        type: typeFilter,
        startDate,
        endDate,
        limit: 100,
      });

      if (result.data.success) {
        setDeadlines(result.data.deadlines);
      } else {
        throw new Error(result.data.error || 'Failed to load deadlines');
      }
    } catch (err) {
      console.error('Error loading deadlines:', err);
      setError(err instanceof Error ? err.message : 'Failed to load deadlines');
    } finally {
      setLoading(false);
    }
  }, [caseFileId, statusFilter, typeFilter, startDate, endDate]);

  useEffect(() => {
    fetchDeadlines();
  }, [fetchDeadlines, refreshKey]);

  const handleComplete = async (deadlineId: string) => {
    setActionLoading(deadlineId);
    try {
      const completeDeadline = httpsCallable<
        { caseFileId: string; deadlineId: string },
        CompleteDeadlineResponse
      >(functions, 'completeDeadline');

      const result = await completeDeadline({ caseFileId, deadlineId });

      if (result.data.success) {
        // Refresh the list
        fetchDeadlines();
      } else {
        throw new Error(result.data.error || 'Failed to complete deadline');
      }
    } catch (err) {
      console.error('Error completing deadline:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete deadline');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (deadlineId: string) => {
    setActionLoading(deadlineId);
    try {
      const dismissDeadline = httpsCallable<
        { caseFileId: string; deadlineId: string },
        DismissDeadlineResponse
      >(functions, 'dismissDeadline');

      const result = await dismissDeadline({ caseFileId, deadlineId });

      if (result.data.success) {
        // Refresh the list
        fetchDeadlines();
      } else {
        throw new Error(result.data.error || 'Failed to dismiss deadline');
      }
    } catch (err) {
      console.error('Error dismissing deadline:', err);
      setError(err instanceof Error ? err.message : 'Failed to dismiss deadline');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Group deadlines by status
  const groupedDeadlines: GroupedDeadlines = {
    overdue: deadlines.filter((d) => d.status === 'overdue'),
    due_soon: deadlines.filter((d) => d.status === 'due_soon'),
    upcoming: deadlines.filter((d) => d.status === 'upcoming'),
    completed: deadlines.filter((d) => d.status === 'completed'),
    dismissed: deadlines.filter((d) => d.status === 'dismissed'),
  };

  const sections = [
    { key: 'overdue', label: 'Overdue', emoji: '', color: 'text-red-600' },
    { key: 'due_soon', label: 'Due Soon', emoji: '', color: 'text-orange-600' },
    { key: 'upcoming', label: 'Upcoming', emoji: '', color: 'text-green-600' },
    { key: 'completed', label: 'Completed', emoji: '', color: 'text-gray-500' },
    { key: 'dismissed', label: 'Dismissed', emoji: '', color: 'text-gray-400' },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        <DeadlineCardSkeleton />
        <DeadlineCardSkeleton />
        <DeadlineCardSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle size={24} />
            <div>
              <p className="font-medium">Error loading deadlines</p>
              <p className="text-sm text-muted">{error}</p>
            </div>
          </div>
          <button onClick={fetchDeadlines} className="btn-secondary flex items-center gap-2">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (deadlines.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-soft-neutral flex items-center justify-center">
            <Calendar size={32} className="text-muted" />
          </div>
          <h3 className="text-lg font-semibold text-ink mb-2">No deadlines found</h3>
          <p className="text-muted mb-4">
            {statusFilter || typeFilter || startDate || endDate
              ? 'No deadlines match your current filters.'
              : 'Create your first deadline to start tracking important dates.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => {
        const sectionDeadlines = groupedDeadlines[section.key as keyof GroupedDeadlines];
        const isCollapsed = collapsedSections[section.key];
        const count = sectionDeadlines.length;

        if (count === 0) return null;

        return (
          <div key={section.key}>
            {/* Section header */}
            <button
              onClick={() => toggleSection(section.key)}
              className="flex items-center gap-2 w-full text-left mb-3 group"
            >
              {isCollapsed ? (
                <ChevronRight size={18} className="text-muted" />
              ) : (
                <ChevronDown size={18} className="text-muted" />
              )}
              <h3 className={`font-semibold ${section.color}`}>
                {section.emoji} {section.label}
              </h3>
              <span className="badge badge-secondary text-xs">{count}</span>
            </button>

            {/* Section content */}
            {!isCollapsed && (
              <div className="space-y-3 pl-6">
                {sectionDeadlines.map((deadline) => (
                  <div
                    key={deadline.deadlineId}
                    className={actionLoading === deadline.deadlineId ? 'opacity-50 pointer-events-none' : ''}
                  >
                    <DeadlineCard
                      deadline={deadline}
                      onClick={onDeadlineClick ? () => onDeadlineClick(deadline.deadlineId) : undefined}
                      onComplete={
                        deadline.status !== 'completed' && deadline.status !== 'dismissed'
                          ? handleComplete
                          : undefined
                      }
                      onDismiss={
                        deadline.status !== 'completed' && deadline.status !== 'dismissed'
                          ? handleDismiss
                          : undefined
                      }
                      onEdit={onEditClick}
                      onExport={onExportClick}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default DeadlinesList;
